import type {
  CreateListItemDto,
  List,
  ListItem,
  ListWithItems,
  UpdateListItemDto,
} from '@mrrmlab/shared-types';
import type {
  AddShoppingListItemDto,
  ShoppingList,
  ShoppingListItem,
  ShoppingListWithItems,
  UpdateShoppingListItemDto,
} from './types';

type ShoppingItemData = Extract<ListItem['data'], { type: 'shopping' }>;

export function toShoppingList(list: List): ShoppingList | null {
  if (list.type !== 'shopping') return null;
  const { type: _type, position: _position, ...rest } = list;
  return rest;
}

export function toShoppingListItem(item: ListItem): ShoppingListItem | null {
  if (item.data.type !== 'shopping') return null;
  const { title, checked, quantity, description, departmentId } = item.data;
  const base: ShoppingListItem = {
    id: item.id,
    listId: item.listId,
    title,
    checked,
    missing: item.data.missing ?? false,
    quantity,
    departmentId: departmentId ?? null,
    position: item.position,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
  return description !== undefined ? { ...base, description } : base;
}

export function toShoppingListWithItems(
  list: ListWithItems,
): ShoppingListWithItems | null {
  const shopping = toShoppingList(list);
  if (!shopping) return null;
  const items = list.items
    .map(toShoppingListItem)
    .filter((x): x is ShoppingListItem => x !== null);
  return { ...shopping, items };
}

export function toCreateListItemDto(dto: AddShoppingListItemDto): CreateListItemDto {
  const data: ShoppingItemData = {
    type: 'shopping',
    title: dto.title,
    checked: false,
    missing: false,
    quantity: dto.quantity,
    ...(dto.description !== undefined ? { description: dto.description } : {}),
    ...(dto.departmentId !== undefined ? { departmentId: dto.departmentId } : {}),
  };
  return dto.position !== undefined ? { data, position: dto.position } : { data };
}

/**
 * Builds a generic update DTO from a shopping-style partial patch. The generic
 * API takes a full `data` payload (no partial inside `data`), so we merge the
 * patch onto `current`. `description: null` is the legacy-shape signal to clear
 * the field and drops it from `data` entirely. When the patch only touches
 * `position`, `data` is omitted to avoid a pointless rewrite.
 */
export function toUpdateListItemDto(
  patch: UpdateShoppingListItemDto,
  current: ShoppingItemData,
): UpdateListItemDto {
  const touchesData =
    patch.title !== undefined ||
    patch.checked !== undefined ||
    patch.missing !== undefined ||
    patch.quantity !== undefined ||
    patch.description !== undefined;

  const result: UpdateListItemDto = {};

  if (touchesData) {
    const description =
      patch.description === null
        ? undefined
        : patch.description !== undefined
          ? patch.description
          : current.description;
    const data: ShoppingItemData = {
      type: 'shopping',
      title: patch.title ?? current.title,
      checked: patch.checked ?? current.checked,
      missing: patch.missing ?? current.missing ?? false,
      quantity: patch.quantity ?? current.quantity,
      ...(description !== undefined ? { description } : {}),
    };
    result.data = data;
  }

  if (patch.position !== undefined) {
    result.position = patch.position;
  }

  return result;
}
